import { createSelector } from "@reduxjs/toolkit";

import type {
  CommitDto,
  DeleteApplicationResponseBody,
  GetAllApplicationsResponseBody,
  GetApplicationCommitsResponseBody,
  IHomepageApplicationV2Dto,
  PutApplicationUpdateResponseBody,
} from "@superblocksteam/shared";

import { reduxApiSlice } from "./index";

const extendedApi = reduxApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listApplications: builder.query<IHomepageApplicationV2Dto[], void>({
      query: () => ({
        url: `/v2/applications`,
        method: "GET",
      }),
      transformResponse: (response: { data: GetAllApplicationsResponseBody }) =>
        response.data.applications,
    }),

    updateApplicationMetadata: builder.mutation<
      PutApplicationUpdateResponseBody,
      {
        id: string;
        name?: string;
        folderId?: string | null;
      }
    >({
      query: ({ id, ...updates }) => ({
        url: `/v2/applications/${id}/metadata`,
        method: "PUT",
        body: updates,
      }),
      transformResponse: (response: {
        data: PutApplicationUpdateResponseBody;
      }) => response.data,

      async onQueryStarted({ id, ...updates }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          extendedApi.util.updateQueryData(
            "listApplications",
            undefined,
            (draft) => {
              const itemIndex = draft.findIndex((item) => item.id === id);
              if (itemIndex !== -1) {
                draft[itemIndex] = {
                  ...draft[itemIndex],
                  name: updates.name ?? draft[itemIndex].name,
                  folderId:
                    updates.folderId === undefined // specifically test for undefined. A null value is used to indicate the root folder.
                      ? draft[itemIndex].folderId
                      : updates.folderId,
                };
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    deleteApplication: builder.mutation<
      DeleteApplicationResponseBody,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/v2/applications/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          extendedApi.util.updateQueryData(
            "listApplications",
            undefined,
            (draft) => {
              const index = draft.findIndex((item) => item.id === id);
              if (index !== -1) {
                draft.splice(index, 1);
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    getAppCommits: builder.query<CommitDto[], { applicationId: string }>({
      query: ({ applicationId }) => ({
        url: `/v2/applications/${applicationId}/commits`,
        method: "GET",
        params: { limit: 10, offset: 0, commitType: "autosave" },
      }),
      transformResponse: (response: {
        data: GetApplicationCommitsResponseBody;
      }) => response.data.autosaves,
    }),

    getScopedToken: builder.query<string, { applicationId: string }>({
      query: ({ applicationId }) => ({
        url: `/v1/token/scoped`,
        method: "POST",
        body: { applicationId, scope: "apps:update" },
      }),
      transformResponse: (response: { access_token: string }) =>
        response.access_token,
      keepUnusedDataFor: 300,
    }),
  }),
});

export const {
  useListApplicationsQuery,
  useUpdateApplicationMetadataMutation,
  useDeleteApplicationMutation,
  useGetAppCommitsQuery,
  useGetScopedTokenQuery,
  util: applicationsApiUtil,
} = extendedApi;

export const getApplications = createSelector(
  extendedApi.endpoints.listApplications.select(),
  (applications) => applications.data ?? [],
);

export const getApplicationsStatus = createSelector(
  extendedApi.endpoints.listApplications.select(),
  (applications) => ({
    isLoading: applications.isLoading,
    isSuccess: applications.isSuccess,
    isError: applications.isError,
    error: applications.error,
  }),
);

export const applicationsApi = extendedApi;
